import {useEffect, useRef} from 'react';
import {dismissIOSSafariView, getOauthUrl, openLoginWindow} from '@Libraries/login-signup-library';
import {GA4EventName} from '@Libraries/ga-events';
import type {AjaxSuccessResponse} from '@Utils/ajax.util';
import {isIOSAppOrEmbeddedEditor} from '@Utils/browser.util';
import {getStateFromUrl} from '@Utils/url.util';

const APPLE_OAUTH_DONE_METHOD = 'oauthDone';
const APPLE_OAUTH_ORIGIN = 'https://appleid.apple.com';

type ExistingSsoAccountHandler = (identityToken: string, email: string, password: string) => Promise<void>;
type AppleMessageListener = (event: MessageEvent) => void;

interface AppleOauthMessage {
  method: string;
  data: {
    authorization: {
      code: string;
      state: string;
    };
  };
}

interface UseAppleSignInParams {
  redirectUrl?: string;
  onSignInDone?: (userId: string) => void;
}

interface UseAppleSignInReturn {
  /** Opens Apple's consent window and finishes the log-in or sign-up once Apple posts the authorization back. */
  signInWithApple: () => Promise<void>;
}

/**
 * The Apple OAuth handshake that `AppleLogin` and the saved-accounts `Continue` share. Apple posts the
 * authorization to the opener as a `message` event instead of the cookie the other providers use; the iOS
 * app and the embedded editor get neither and ask the server instead.
 */
export const useAppleSignIn = ({redirectUrl = '', onSignInDone}: UseAppleSignInParams = {}): UseAppleSignInReturn => {
  const oauthState = useRef('');
  const messageListener = useRef<AppleMessageListener | null>(null);

  useEffect(() => {
    return (): void => {
      removeMessageListener();
    };
  }, []);

  const removeMessageListener = (): void => {
    if (messageListener.current !== null) {
      window.removeEventListener('message', messageListener.current);
      messageListener.current = null;
    }
  };

  const onLoginUrlFetched = (url: string): void => {
    oauthState.current = getStateFromUrl(url);
  };

  const handleExistingAccount: ExistingSsoAccountHandler = async (identityToken, email, password) => {
    const response = (await window.PMW.writeLocal('authenticate/handleExistingSSOAccount', {
      identityToken,
      email,
      password,
      source: GA4EventName.SIGN_UP_APPLE,
    })) as AjaxSuccessResponse;
    window.PMW.ssoAccountAuthenticationCallback(response, handleExistingAccount, redirectUrl, onSignInDone, GA4EventName.SIGN_UP_APPLE);
  };

  const checkLoggedInStatus = async (): Promise<void> => {
    const response = (await window.PMW.pollingAjaxCallAsync(
      (): Promise<void> => {
        return Promise.resolve();
      },
      () => {
        return window.PMW.readLocal('authenticate/hasAccountBeenLoggedIn', {state: oauthState.current});
      }
    )) as AjaxSuccessResponse;
    dismissIOSSafariView();
    window.PMW.ssoAccountAuthenticationCallback(response, handleExistingAccount, redirectUrl, onSignInDone, GA4EventName.SIGN_UP_APPLE);
  };

  const onAppleMessage = (loginWindow: Window | null, event: MessageEvent): void => {
    if (event.origin !== APPLE_OAUTH_ORIGIN) {
      return;
    }
    const message = JSON.parse(String(event.data)) as AppleOauthMessage;
    if (message.method !== APPLE_OAUTH_DONE_METHOD || !message.data?.authorization) {
      return;
    }
    removeMessageListener();
    void window.PMW.writeLocal('authenticate/appleOauthCallback', {
      state: message.data.authorization.state,
      code: message.data.authorization.code,
    }).then((response) => {
      loginWindow?.close();
      window.PMW.ssoAccountAuthenticationCallback(response, handleExistingAccount, redirectUrl, onSignInDone, GA4EventName.SIGN_UP_APPLE);
    });
  };

  const signInWithApple = async (): Promise<void> => {
    const loginWindow = await openLoginWindow(getOauthUrl.bind(null, 'authenticate/getAppleOauthUrl'), onLoginUrlFetched);
    if (isIOSAppOrEmbeddedEditor()) {
      void checkLoggedInStatus();
      return;
    }
    removeMessageListener();
    messageListener.current = onAppleMessage.bind(null, loginWindow);
    window.addEventListener('message', messageListener.current);
  };

  return {signInWithApple};
};
